import React from "react";

import {
  ProposalQuoteSection,
  RateComponent,
  SupplierQuote,
} from "../../../types/proposalDocuments.types";
import {
  getSectionAnchorId,
  getSectionDisplayTitle,
} from "../../../utils/proposalDocuments/quoteOutline";
import { getTermFraction } from "../../../utils/proposalDocuments/term";
import { AVAILABLE_RATES, AVAILABLE_UNITS, RateUnit } from "../proposalDocuments.constants";
import { useProposalDocument } from "../proposalDocumentsContext";

import CalculatedField from "./CalculatedField";
import SectionDetailsFields from "./sectionDetailsFields";
import SupplierQuoteCard from "./supplierQuoteCard";
import { useCollapsible } from "./useCollapsible";

interface QuoteSectionProps {
  section: ProposalQuoteSection;
  onChange: (updated: ProposalQuoteSection) => void;
  onRemove: () => void;
  sectionIndex: number;
}
const getUnitById = (id: string): RateUnit | undefined => AVAILABLE_UNITS.find((u) => u.id === id);

export const makeEmptyRate = (label?: string): RateComponent => {
  const rateType = AVAILABLE_RATES.find((r) => r.label === label) ?? AVAILABLE_RATES[0];
  const defaultUnit = getUnitById(rateType.defaultRate);
  return {
    label: rateType.label,
    value: 0,
    consumption: 0,
    unit: defaultUnit ? defaultUnit.label : "",
    hasConsumption: defaultUnit ? defaultUnit.hasConsumption : false,
    nonCommodity: rateType.nonCommodity,
    kvaPeriod: "daily",
    hidden: false,
    estimatedAnnualCost: 0,
  };
};

const getEmptyQuote = (): SupplierQuote => ({
  rates: [makeEmptyRate()],
  mpan: null,
  mprn: null,
  zoho_meter_id: null,
  estimated_annual_cost: null,
  annual_consumption: null,
  uplift: 0,
  notes: "",
});

// Sums the per-meter (proposed quote) full-year totals, then scales
// them down to the actual contract length. A section is one contract
// that can cover multiple meters — meter-level figures always stay
// full-year for comparison, this rollup is the only place term length
// affects the numbers.
const computeSectionTotals = (proposed: SupplierQuote[], term: string | null) => {
  const rawConsumption = proposed.reduce((sum, q) => sum + (q.annual_consumption ?? 0), 0);
  const rawCost = proposed.reduce((sum, q) => sum + (q.estimated_annual_cost ?? 0), 0);

  const fraction = getTermFraction(term);

  return {
    annual_consumption: rawConsumption * fraction,
    estimated_annual_cost: rawCost * fraction,
  };
};

const QuoteSection: React.FC<QuoteSectionProps> = ({
  section,
  onChange,
  onRemove,
  sectionIndex,
}) => {
  const { fieldErrors } = useProposalDocument();

  // A stable id base for labels/htmlFor, now that section.title doesn't exist
  const idBase = getSectionAnchorId(sectionIndex);

  // Any error under this section — either its own term, or one of its
  // proposed quotes — used to highlight the section and auto-expand it
  const termError = fieldErrors[`term-${sectionIndex.toString()}`];
  const sectionHasError =
    !!termError || Object.keys(fieldErrors).some((k) => k.startsWith(`proposed-${sectionIndex.toString()}-`));

  const { isCollapsed, toggle } = useCollapsible({
    id: idBase,
    hasError: sectionHasError,
  });

  // Whether this section's term is shorter than a full year — drives
  // the calculated field labels so a bridge contract doesn't get
  // mislabelled as "Annual"
  const isPartialTerm = getTermFraction(section.term) < 1;
  const consumptionLabel = isPartialTerm
    ? `Consumption (${section.term ?? "term"})`
    : "Annual Consumption";
  const costLabel = isPartialTerm
    ? `Estimated Cost (${section.term ?? "term"})`
    : "Estimated Annual Cost";
  // Explains the figure below it when it's been scaled down from a full
  // year to the contract term - otherwise it looks like it doesn't add up
  // against the (always full-year) per-quote totals shown further down
  const scaledNote = isPartialTerm
    ? `* Scaled to the ${section.term ?? ""} term - quote options below show full-year figures.`
    : undefined;

  // Section-level fields that replaced the removed per-quote product/contract_type/term
  const handleSupplierChange = (supplier: string) => {
    onChange({ ...section, supplier });
  };

  const handleProductChange = (product: string) => {
    onChange({ ...section, product });
  };

  const handleContractTypeChange = (contract_type: string) => {
    onChange({ ...section, contract_type });
  };

  const handleCurrentToggle = (checked: boolean) => {
    // Current/rolling suppliers have no fixed term
    onChange({ ...section, current: checked, term: checked ? null : section.term });
  };

  const handleTermChange = (term: string) => {
    onChange({
      ...section,
      term,
      ...computeSectionTotals(section.proposed, term),
    });
  };

  const handleUpdateProposed = (pIndex: number, updated: SupplierQuote) => {
    const newProposed = [...section.proposed];
    newProposed[pIndex] = updated;
    onChange({
      ...section,
      proposed: newProposed,
      ...computeSectionTotals(newProposed, section.term),
    });
  };

  const handleRemoveProposed = (pIndex: number) => {
    const newProposed = [...section.proposed];
    newProposed.splice(pIndex, 1);
    onChange({
      ...section,
      proposed: newProposed,
      ...computeSectionTotals(newProposed, section.term),
    });
  };

  const handleAddProposed = () => {
    const newProposed = [...section.proposed, getEmptyQuote()];
    onChange({
      ...section,
      proposed: newProposed,
      ...computeSectionTotals(newProposed, section.term),
    });
  };

  const handleSupplierLogoChange = (value: string) => {
    onChange({ ...section, supplier_logo: value === "" ? null : value });
  };

  const handleBadgeChange = (badge: string) => {
    onChange({ ...section, badge: badge || null });
  };

  // Display-only title, mirroring the backend's computed `title` field,
  // so the collapsed header still shows something meaningful
  const displayTitle = getSectionDisplayTitle(section);

  return (
    <article
      id={idBase}
      className={`quote-section-item ${sectionHasError ? "quote-section-item--has-error" : ""}`}
    >
      {/* Section Header */}
      <div className="quote-section-item__header">
        <button
          type="button"
          className={`quote-section-item__collapse-button ${isCollapsed ? "is-collapsed" : ""}`}
          onClick={toggle}
          aria-label={isCollapsed ? "Expand section" : "Collapse section"}
          aria-expanded={!isCollapsed}
        >
          <span className="collapse-icon" aria-hidden="true" />
        </button>
        <span className="quote-section-item__title-display">
          {displayTitle}
          {section.current && (
            <span className="quote-section-item__current-badge">Current Supplier</span>
          )}
        </span>
        <button
          type="button"
          className="quote-section-item__remove-button"
          onClick={onRemove}
          aria-label="Remove this quote section"
        >
          Remove Section
        </button>
      </div>

      {!isCollapsed && (
        <SectionDetailsFields
          idBase={idBase}
          current={section.current}
          onCurrentChange={handleCurrentToggle}
          supplier={section.supplier}
          onSupplierChange={handleSupplierChange}
          product={section.product}
          onProductChange={handleProductChange}
          contractType={section.contract_type}
          onContractTypeChange={handleContractTypeChange}
          term={section.term}
          onTermChange={handleTermChange}
          termError={termError}
          supplierLogo={section.supplier_logo}
          onSupplierLogoChange={handleSupplierLogoChange}
          badge={section.badge}
          onBadgeChange={handleBadgeChange}
        >
          <CalculatedField
            id={`${idBase}-annual-consumption`}
            label={consumptionLabel}
            value={section.annual_consumption ?? 0}
            suffix="kWh"
            decimals={0}
            note={scaledNote}
          />

          <CalculatedField
            id={`${idBase}-estimated-annual-cost`}
            label={costLabel}
            value={section.estimated_annual_cost ?? 0}
            prefix="£"
            note={scaledNote}
          />
        </SectionDetailsFields>
      )}

      {/* Proposed Supplier Quotes */}
      {!isCollapsed && (
        <section className="quote-section-item__subsection">
          <div className="quote-section-item__subsection-header">
            <h3>Proposed Supplier Quotes</h3>
          </div>

          {section.proposed.length === 0 ? (
            <div className="quote-section-item__empty-state">No proposed quotes yet.</div>
          ) : (
            <div className="quote-section-item__list">
              {section.proposed.map((prop, pIndex) => (
                <SupplierQuoteCard
                  key={pIndex}
                  quote={prop}
                  index={pIndex}
                  onChange={(updated) => {
                    handleUpdateProposed(pIndex, updated);
                  }}
                  onRemove={() => {
                    handleRemoveProposed(pIndex);
                  }}
                  sectionIndex={sectionIndex}
                  isCurrent={section.current}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            className="quote-section-item__add-button"
            onClick={handleAddProposed}
            aria-label="Add proposed quote"
          >
            + Add Proposed Quote
          </button>
        </section>
      )}
    </article>
  );
};

export default QuoteSection;
